import type mapboxgl from 'mapbox-gl';
import { environment } from '../../../environments/environment';

/**
 * DOVE-2520 — mapbox-gl is ~1.5 MB raw / ~430 kB gzipped. Imported statically, it downloaded and
 * parsed on every route in the Inventory, Patients and Customer Orders modules, map or not.
 * Loading it here with a dynamic import puts it in its own chunk that only map components ask
 * for; the access token is set once, on first load.
 *
 * Map components keep `import type mapboxgl from 'mapbox-gl'` for their type annotations (erased
 * at build time) and take the runtime library from loadMapbox().
 */
let mapboxPromise: Promise<typeof mapboxgl> | null = null;

export function loadMapbox(): Promise<typeof mapboxgl> {
	mapboxPromise ??= import('mapbox-gl')
		.then((module) => {
			const gl = module.default;
			(gl as any).accessToken = environment.mapboxAccessToken;
			return gl;
		})
		.catch((error) => {
			// Let the next caller retry instead of caching the failure (e.g. a prefetch
			// that ran while offline).
			mapboxPromise = null;
			throw error;
		});

	return mapboxPromise;
}

/**
 * Warm the cache so the first map visit isn't slower than it was when mapbox-gl shipped with
 * the feature modules: once the page has finished loading and the browser is idle, fetch the
 * chunk in the background. A failure here is harmless; the map component loads it again.
 */
export function prefetchMapboxWhenIdle(): void {
	const whenIdle: (callback: () => void) => void =
		(window as any).requestIdleCallback?.bind(window) ?? ((callback: () => void) => setTimeout(callback, 2000));

	const start = () => whenIdle(() => void loadMapbox().catch(() => undefined));

	if (document.readyState === 'complete') {
		start();
	} else {
		window.addEventListener('load', start, { once: true });
	}
}
